import React, { useState } from "react";
import { BookOpen, Download, Loader2, LogIn, LayoutDashboard, Settings2, AlertTriangle, FileArchive, Sparkles, ShieldCheck, Leaf, FolderOpen, Vote, ScrollText, FileText, GraduationCap, ShoppingCart, Stethoscope } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/CrudModule";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const HERO = "/valve-technician.jpg";

const SECTIONS = [
  { icon: LogIn, title: "1. Acesso ao sistema", steps: [
    "Abra o portal e clique em 'Login' no canto superior direito.",
    "Informe seu e-mail corporativo e senha. Esqueceu? Use 'Esqueceu sua senha?'.",
    "O Administrador cadastra usuários e define o perfil: Visualizador (só vê), Editor (sobe/edita), Gestor (gerencia) e Administrador (tudo + usuários)." ] },
  { icon: LayoutDashboard, title: "2. Dashboard Executivo", steps: [
    "O placar mostra os dias sem acidentes (contagem automática) e o recorde.",
    "TF e TG são calculados pela NBR 14280 a partir dos dados de Configurações.",
    "Cartões: DDS do mês, quase-acidentes, EPIs vencidos e resíduos destinados." ] },
  { icon: Settings2, title: "3. Configuração inicial (uma única vez)", steps: [
    "Em Configurações, defina a 'Data de início do contador' de dias sem acidentes.",
    "Informe metas de TF/TG e DDS programados por mês.",
    "Preencha horas trabalhadas, acidentes com afastamento e dias perdidos." ] },
  { icon: AlertTriangle, title: "4. Registrar um acidente", steps: [
    "No Dashboard, clique em 'Registrar acidente'.",
    "Informe data, descrição, se houve afastamento e dias perdidos.",
    "O placar zera e reinicia automaticamente; o período anterior vai para o histórico." ] },
  { icon: FileArchive, title: "5. Importação em lote (ZIP)", steps: [
    "Em QUALQUER módulo use 'Importar ZIP' para alimentar o sistema com muitos arquivos de uma vez.",
    "Cada arquivo do ZIP vira um registro com o documento já anexado.",
    "No módulo ISO 14001, o sistema lista os arquivos do ZIP e permite apontar cada um a um item do checklist.",
    "Depois é só abrir cada registro e completar os campos." ] },
  { icon: Sparkles, title: "6. Leitura por IA", steps: [
    "Em Colaboradores, 'Importar PDF (IA)' lê a lista de funcionários e cadastra todos.",
    "Em Documentos, 'Analisar com IA' lê licenças (CETESB, AVCB, CADRI) e laudos.",
    "Revise e corrija os campos sugeridos antes de salvar." ] },
  { icon: ShieldCheck, title: "7. Segurança do Trabalho", steps: [
    "DDS: registre diálogos com tema, setor, facilitador e lista de presença.",
    "EPIs (NR-06): controle entregas por colaborador, CA e validade, com assinatura eletrônica.",
    "Ocorrências: investigue com 5 Porquês, Ishikawa e plano de ação 5W2H.",
    "Treinamentos e Inspeções: capacitações (NRs) e checklists (NR-12/13, bancadas, SPDA).",
    "CIPA: cadastre os candidatos, abra a votação e acompanhe a eleição (ver seção 10)." ] },
  { icon: Leaf, title: "8. Meio Ambiente (ISO 14001)", steps: [
    "Resíduos: registre geração, MTR e certificado de destinação (CDF).",
    "Gestão ISO 14001: acompanhe o checklist por seção com barra de progresso.",
    "Aspectos e Impactos: monte a matriz e vincule aos itens da norma.",
    "Licenças: controle CETESB, AVCB, CADRI e outras, com responsável e status de renovação.",
    "Painel Ambiental: veja licenças a vencer e resíduos por mês." ] },
  { icon: Vote, title: "10. CIPA — Eleição Eletrônica", steps: [
    "Cadastre os candidatos com nome, cargo, setor, chapa e foto.",
    "O gestor abre ou encerra a votação a qualquer momento.",
    "Cada usuário vota uma única vez; a apuração é automática e em tempo real.",
    "Clique em 'Ata (PDF)' para gerar a ata de apuração com o resultado final." ] },
  { icon: FileText, title: "11. Relatórios e alertas", steps: [
    "No Dashboard, 'Relatório do mês' gera um PDF (você escolhe o mês) pronto para o cliente.",
    "O sino no topo lista tudo vencido ou a vencer em 30 dias (documentos, EPI, ASO, licenças, ISO).",
    "Use 'Exportar PDF' e 'Exportar Excel' em cada módulo para auditorias externas.",
    "Baixe este manual em PDF (botão acima) para treinar e distribuir à equipe." ] },
  { icon: Stethoscope, title: "5b. Medicina — Importar ASO por IA", steps: [
    "Em Medicina do Trabalho, clique em 'Importar ZIP' e envie um .zip com os ASOs em PDF.",
    "A IA lê cada ASO e preenche colaborador, tipo de exame, resultado, médico, data e validade.",
    "A validade usa a MENOR data de vencimento entre os exames do ASO.",
    "Acompanhe a barra 'Lendo ASOs com IA'; ao final a tabela aparece preenchida (sem '—')." ] },
  { icon: GraduationCap, title: "12. Escola Online (EAD)", steps: [
    "Em Treinamentos → Escola Online, cadastre matrículas por colaborador e curso (NRs).",
    "No computador o aluno entra só com login e senha; no celular é feita a foto de presença.",
    "Cada matrícula gera um QR de 1 toque e envia e-mail de acesso ao colaborador.",
    "Curso de 8h (vídeo + leitura), prova de 5 questões e certificado em PDF assinado em /assinaturas." ] },
  { icon: ShoppingCart, title: "13. Venda de cursos (/cursos)", steps: [
    "A vitrine pública fica em /cursos (botão 'Cursos' na tela inicial).",
    "O aluno externo escolhe a NR, paga com cartão (Stripe) e é matriculado automaticamente.",
    "Recebe o link de acesso por e-mail e, ao concluir, emite o mesmo certificado.",
    "Colaboradores Valbrac têm acesso direto; externos acessam via compra." ] },
];

const IMAGES = ["dashboard", "dashboard", "configuracoes", "dashboard", "documentos", "colaboradores", "epis", "iso14001", "documentos", "cipa", "painel"];

export default function Tutorial() {
  const [busy, setBusy] = useState(false);
  const downloadPdf = async () => {
    setBusy(true);
    try {
      const res = await api.get("/tutorial/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "Manual_SSMA_Valbrac.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Falha ao gerar o PDF"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="tutorial-page">
      <PageHeader icon={BookOpen} title="Tutorial de Uso" subtitle="Guia completo para treinar e capacitar os usuários do Portal SSMA">
        <Button onClick={downloadPdf} disabled={busy} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="tutorial-pdf-btn">
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Baixar manual em PDF
        </Button>
      </PageHeader>

      <div className="mb-6 overflow-hidden rounded-2xl border border-[#E2E8F0] shadow-sm">
        <div className="relative h-44 bg-[#0B2545] sm:h-56">
          <img src={HERO} alt="" className="h-full w-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B2545] via-[#0B2545]/70 to-transparent" />
          <div className="absolute inset-0 flex flex-col justify-center px-6 sm:px-10">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400">Portal SSMA Valbrac</p>
            <h2 className="mt-1 max-w-lg text-2xl font-black text-white sm:text-3xl">Do zero à operação — em poucos passos</h2>
            <p className="mt-1 max-w-md text-sm text-slate-200">Distribua este guia para a equipe. Também disponível em PDF para impressão.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {SECTIONS.map((s, idx) => (
          <div key={s.title} className="rounded-xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600"><s.icon className="h-5 w-5" /></div>
              <h3 className="text-base font-semibold text-[#134074]">{s.title}</h3>
            </div>
            <ul className="space-y-2">
              {s.steps.map((st, i) => (
                <li key={i} className="flex gap-2 text-sm text-slate-600">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#0B2545] text-[10px] font-bold text-white">{i + 1}</span>
                  <span>{st}</span>
                </li>
              ))}
            </ul>
            {IMAGES[idx] && (
              <a href={`/shots/${IMAGES[idx]}.jpg`} target="_blank" rel="noreferrer" className="mt-4 block overflow-hidden rounded-lg border border-slate-200 ring-1 ring-slate-100 transition-all hover:ring-[#134074]/30">
                <img src={`/shots/${IMAGES[idx]}.jpg`} alt={s.title} loading="lazy" className="w-full" />
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
